import { Capacitor } from "@capacitor/core";
import {
    AppUpdate,
    AppUpdateAvailability,
    FlexibleUpdateInstallStatus,
} from "@capawesome/capacitor-app-update";

let flexibleUpdateListenerHandle = null;

/**
 * Checks the Play Store for an available update and installs it
 * automatically, without requiring any user interaction.
 *
 * - If Google Play allows an "immediate" update, Play itself takes over
 *   with its full-screen update flow: it downloads, installs, and
 *   restarts the app.
 * - Otherwise, if a "flexible" update is allowed, the update is
 *   downloaded in the background and, as soon as the download finishes,
 *   it is installed automatically (the app restarts) — no prompt is
 *   shown to the user.
 *
 * Android only. This is a no-op on iOS/web, where Google Play's in-app
 * update APIs don't apply.
 */
export const checkForAppUpdate = async () => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
        return;
    }

    try {
        const info = await AppUpdate.getAppUpdateInfo();

        if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
            return;
        }

        if (info.immediateUpdateAllowed) {
            await AppUpdate.performImmediateUpdate();
            return;
        }

        if (info.flexibleUpdateAllowed) {
            await startFlexibleAutoInstall();
        }
    } catch (error) {
        console.error("App update check failed:", error);
    }
};

const startFlexibleAutoInstall = async () => {
    // Avoid attaching more than one listener if this is called again
    // (e.g. on every app resume) while a download is already in flight.
    if (flexibleUpdateListenerHandle) return;

    flexibleUpdateListenerHandle = await AppUpdate.addListener(
        "onFlexibleUpdateStateChange",
        async (state) => {
            if (state.installStatus !== FlexibleUpdateInstallStatus.DOWNLOADED) {
                return;
            }

            await flexibleUpdateListenerHandle?.remove();
            flexibleUpdateListenerHandle = null;

            // Downloaded in the background — install and restart the
            // app automatically, with no user action required.
            await AppUpdate.completeFlexibleUpdate();
        },
    );

    await AppUpdate.startFlexibleUpdate();
};
